import React, { useState } from 'react';
import { Building2, AlertCircle, CheckCircle, Mail, ArrowLeft } from 'lucide-react';

export default function ForgotPasswordPage({ setAuthPage, onReset }) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    setError('');
    const result = await onReset(email);
    setIsLoading(false);
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .auth-input { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: white; }
        .auth-input:focus { outline: none; border-color: #3b82f6; background: rgba(255,255,255,0.1); }
        .auth-input::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button
            onClick={() => setAuthPage('landing')}
            className="inline-flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Building2 size={24} className="text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-white tracking-tight">FlipperPro</h1>
              <p className="text-xs text-blue-300 font-medium tracking-wide uppercase"></p>
            </div>
          </button>
        </div>

        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
              <p className="text-blue-200 text-sm mb-6">
                We've sent a password reset link to
                <br />
                <span className="font-semibold text-white">{email}</span>
              </p>
              <button
                onClick={() => setAuthPage('signin')}
                className="text-blue-400 hover:text-blue-300 text-sm font-medium"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">Reset password</h2>
                <p className="text-blue-200 text-sm">Enter your email to receive a reset link</p>
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 mb-4 flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-400" />
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="auth-input w-full pl-12 pr-4 py-3.5 rounded-xl text-sm"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setAuthPage('signin')}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-2 mx-auto"
                >
                  <ArrowLeft size={16} /> Back to Sign In
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
